// S18B-1 — Asset register API hooks (OSWC GAP-7).
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export type DeviceType = "camera" | "nvr" | "switch" | "ups" | "router" | "other";

export interface Asset {
  id: string;
  tenant_id: string;
  site_id: string;
  parent_asset_id: string | null;
  device_type: DeviceType;
  asset_tag: string | null;
  vendor: string | null;
  model: string | null;
  serial_no: string | null;
  install_date: string | null;
  warranty_expiry: string | null;
  linked_camera_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssetList {
  items: Asset[];
  total: number;
}

export interface AssetTreeNode extends Asset {
  children: AssetTreeNode[];
}

export interface AssetTree {
  site_id: string;
  nodes: AssetTreeNode[];
}

export interface AssetCreateInput {
  site_id: string;
  device_type: DeviceType;
  parent_asset_id?: string | null;
  asset_tag?: string | null;
  vendor?: string | null;
  model?: string | null;
  serial_no?: string | null;
  install_date?: string | null;
  warranty_expiry?: string | null;
  linked_camera_id?: string | null;
  notes?: string | null;
}

const assetsKey = ["assets"] as const;

export interface AssetFilters {
  site_id?: string;
  device_type?: string;
  vendor?: string;
  asset_tag?: string;
}

export function useAssets(filters?: AssetFilters) {
  return useQuery({
    queryKey: [...assetsKey, "list", filters ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.site_id) params.set("site_id", filters.site_id);
      if (filters?.device_type) params.set("device_type", filters.device_type);
      if (filters?.vendor) params.set("vendor", filters.vendor);
      if (filters?.asset_tag) params.set("asset_tag", filters.asset_tag);
      const { data } = await api.get<AssetList>(
        `/api/v1/assets?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useAssetTree(siteId: string | undefined) {
  return useQuery({
    queryKey: [...assetsKey, "tree", siteId],
    queryFn: async () => {
      const { data } = await api.get<AssetTree>(
        `/api/v1/assets/tree?site_id=${siteId}`,
      );
      return data;
    },
    enabled: Boolean(siteId),
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssetCreateInput) => {
      const { data } = await api.post<Asset>("/api/v1/assets", input);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetsKey });
    },
  });
}

export function useUpdateAsset(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<AssetCreateInput>) => {
      const { data } = await api.patch<Asset>(`/api/v1/assets/${id}`, patch);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetsKey });
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/assets/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetsKey });
    },
  });
}

// Binary export (PDF / Excel) — returns a Blob for download.
export async function exportAssets(
  siteId: string,
  format: "pdf" | "excel",
): Promise<Blob> {
  const { data } = await api.get<Blob>(
    `/api/v1/assets/export?site_id=${siteId}&format=${format}`,
    { responseType: "blob" },
  );
  return data;
}
